import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

export interface LightboxItem {
  url: string;
  kind: 'image' | 'video';
  title?: string;
}

interface LightboxProps {
  items: LightboxItem[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

/** Visualizador em tela cheia (imagem ou vídeo) com anterior/próxima (setas do teclado, Esc fecha). */
export const Lightbox: React.FC<LightboxProps> = ({ items, index, onIndexChange, onClose }) => {
  const item = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'ArrowLeft' && hasPrev) onIndexChange(index - 1);
      else if (e.key === 'ArrowRight' && hasNext) onIndexChange(index + 1);
    };
    // captura: o Esc fecha só o lightbox, não o drawer por baixo
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [index, hasPrev, hasNext, onClose, onIndexChange]);

  if (!item) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col animate-fade-in" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm truncate">
          {item.title}
          {items.length > 1 && <span className="text-slate-400 ml-2">{index + 1} / {items.length}</span>}
        </span>
        <button type="button" onClick={onClose} aria-label="Fechar" className="p-2 rounded-full hover:bg-white/10">
          <X size={22} />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center relative min-h-0 px-12 pb-6">
        {hasPrev && (
          <button
            type="button"
            aria-label="Anterior"
            onClick={(e) => {
              e.stopPropagation();
              onIndexChange(index - 1);
            }}
            className="absolute left-2 p-2 rounded-full text-white hover:bg-white/10"
          >
            <ChevronLeft size={32} />
          </button>
        )}
        <div onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full flex items-center justify-center">
          {item.kind === 'video' ? (
            <video key={item.url} src={item.url} controls autoPlay className="max-h-[85vh] max-w-full" />
          ) : (
            <img key={item.url} src={item.url} alt={item.title ?? ''} className="max-h-[85vh] max-w-full object-contain" />
          )}
        </div>
        {hasNext && (
          <button
            type="button"
            aria-label="Próxima"
            onClick={(e) => {
              e.stopPropagation();
              onIndexChange(index + 1);
            }}
            className="absolute right-2 p-2 rounded-full text-white hover:bg-white/10"
          >
            <ChevronRight size={32} />
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default Lightbox;
